import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import type { FileNode, ShareLink } from "../../api.ts";
import { formatDate, formatDateTime, formatShortDate } from "../../lib/format.ts";
import { messageOf, sharesQuery, useCreateShare, useRevokeShare } from "../../lib/queries.ts";
import {
  dateInputValue,
  EXPIRY_CHOICES,
  type ExpiryChoice,
  endedAt,
  expiryOf,
  formatRelative,
  SHARE_PASSWORD_MIN,
  shortShareUrl,
  splitShares,
} from "../../lib/shares.ts";
import { ConfirmDialog } from "../ConfirmDialog.tsx";
import { CopyButton } from "../CopyButton.tsx";
import { AccessOption } from "../file/AccessPanel.tsx";
import filePage from "../file/FilePage.module.css";
import { Icon } from "../Icon.tsx";
import ui from "../ui.module.css";
import styles from "./ShareLinks.module.css";

type Props = { node: FileNode; embedded?: boolean };

export function ShareLinksPanel({ node, embedded = false }: Props) {
  const shares = useQuery(sharesQuery(node.id));
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null);
  const [doomed, setDoomed] = useState<ShareLink | null>(null);
  const revoke = useRevokeShare(node.id);
  const headingId = useId();

  const content = () => {
    if (shares.isPending) return <SharesPending />;
    if (shares.isError) {
      return (
        <div className={styles.problem} role="alert">
          <Icon name="alert" className={styles.problemIcon} />
          <p className={styles.problemText}>
            <strong>Share links did not load.</strong> {messageOf(shares.error)}
          </p>
          <button
            type="button"
            className={ui.button}
            onClick={() => void shares.refetch()}
            disabled={shares.isFetching}
          >
            <Icon name="retry" />
            {shares.isFetching ? "Trying…" : "Try again"}
          </button>
        </div>
      );
    }

    const { active, ended } = splitShares(shares.data.items);
    return (
      <>
        {creating ? (
          <CreateShareForm
            node={node}
            onCancel={() => setCreating(false)}
            onCreated={(token) => {
              setCreating(false);
              setFresh(token);
            }}
          />
        ) : active.length === 0 ? (
          <SharesEmpty node={node} hasEnded={ended.length > 0} onCreate={() => setCreating(true)} />
        ) : null}

        {active.length > 0 ? (
          <ul className={styles.list}>
            {active.map((share) => (
              <ShareRow
                key={share.token}
                share={share}
                fresh={share.token === fresh}
                onRevoke={() => setDoomed(share)}
              />
            ))}
            {creating ? null : (
              <li className={styles.addRow}>
                <button
                  type="button"
                  className={styles.add}
                  onClick={() => {
                    setFresh(null);
                    setCreating(true);
                  }}
                >
                  <Icon name="plus" />
                  New link
                </button>
              </li>
            )}
          </ul>
        ) : null}

        {ended.length > 0 ? <EndedShares shares={ended} /> : null}
      </>
    );
  };

  return (
    <section
      className={embedded ? `${filePage.panel} ${styles.embedded}` : `${ui.card} ${filePage.panel}`}
      aria-labelledby={embedded ? undefined : headingId}
      aria-label={embedded ? `Share links for ${node.name}` : undefined}
    >
      {embedded ? null : (
        <div className={filePage.panelHead}>
          <h2 id={headingId} className={filePage.panelTitle}>
            Share links
          </h2>
        </div>
      )}
      {content()}
      <ConfirmDialog
        open={doomed !== null}
        title="Revoke this link?"
        confirmLabel={revoke.isPending ? "Revoking…" : "Revoke link"}
        cancelLabel="Keep it"
        pending={revoke.isPending}
        error={revoke.error ? messageOf(revoke.error) : null}
        onCancel={() => {
          setDoomed(null);
          revoke.reset();
        }}
        onConfirm={() => {
          if (doomed) revoke.mutate(doomed.token, { onSuccess: () => setDoomed(null) });
        }}
      >
        <p>
          <span className={styles.dialogUrl}>{doomed ? shortShareUrl(doomed.url) : ""}</span> stops
          opening {node.name} straight away, for everyone holding it. The {node.kind} and its other
          links stay as they are.
        </p>
      </ConfirmDialog>
    </section>
  );
}

function SharesEmpty({
  node,
  hasEnded,
  onCreate,
}: {
  node: FileNode;
  hasEnded: boolean;
  onCreate: () => void;
}) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyMark} aria-hidden="true">
        <Icon name="link" />
      </span>
      <div className={styles.emptyText}>
        <p className={styles.emptyTitle}>{hasEnded ? "No active links" : "No share links yet"}</p>
        <p className={styles.emptyDetail}>
          {node.kind === "folder"
            ? "Anyone holding a share link opens this folder without signing in. Items with their own people list stay out."
            : "Anyone holding a share link opens this file without signing in."}
        </p>
      </div>
      <button type="button" className={`${ui.button} ${styles.emptyAction}`} onClick={onCreate}>
        <Icon name="plus" />
        Create a link
      </button>
    </div>
  );
}

function SharesPending() {
  return (
    <div aria-busy="true">
      <span className={ui.visuallyHidden}>Loading share links</span>
      {["first", "second"].map((row) => (
        <div key={row} className={filePage.pendingRow}>
          <span className={`${ui.skeleton} ${filePage.pendingDot}`} />
          <span className={`${ui.skeleton} ${filePage.pendingLine}`} />
        </div>
      ))}
    </div>
  );
}

function Badges({ share }: { share: ShareLink }) {
  if (share.mode !== "stream" && !share.hasPassword && share.pinnedVersion === null) return null;
  return (
    <ul className={styles.badges}>
      {share.mode === "stream" ? (
        <li data-tone="stream">
          <Icon name="play" className={styles.badgeIcon} />
          Stream only
        </li>
      ) : null}
      {share.hasPassword ? (
        <li data-tone="password">
          <Icon name="lock" className={styles.badgeIcon} />
          Password
        </li>
      ) : null}
      {share.pinnedVersion !== null ? (
        <li data-tone="pinned">
          <Icon name="version" className={styles.badgeIcon} />
          Pinned to version {share.pinnedVersion}
        </li>
      ) : null}
    </ul>
  );
}

const SOON_MS = 86_400_000;

function ShareRow({
  share,
  fresh,
  onRevoke,
}: {
  share: ShareLink;
  fresh: boolean;
  onRevoke: () => void;
}) {
  const now = new Date();
  const soon = share.expiresAt !== null && Date.parse(share.expiresAt) - now.getTime() < SOON_MS;

  return (
    <li className={styles.row} data-fresh={fresh || undefined}>
      <span className={styles.mark} data-mode={share.mode} aria-hidden="true">
        <Icon name={share.mode === "stream" ? "play" : "link"} />
      </span>
      <div className={styles.rowText}>
        {fresh ? <p className={styles.freshNote}>Link ready</p> : null}
        <a
          href={share.url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.url}
          data-full={fresh || undefined}
          title={share.url}
        >
          {fresh ? share.url : shortShareUrl(share.url)}
        </a>
        <Badges share={share} />
        <ul className={styles.facts}>
          <li data-soon={soon || undefined}>
            {share.expiresAt === null ? (
              "No end date"
            ) : (
              <Moment
                iso={share.expiresAt}
                label={`Expires ${formatRelative(share.expiresAt, now)}`}
                exactLabel="Expires"
              />
            )}
          </li>
          <li>
            <Moment
              iso={share.createdAt}
              label={`Created ${formatShortDate(share.createdAt, now)}`}
              exactLabel="Created"
            />
          </li>
          <li>
            {share.lastUsedAt === null ? (
              "Never opened"
            ) : (
              <Moment
                iso={share.lastUsedAt}
                label={`Opened ${formatRelative(share.lastUsedAt, now)}`}
                exactLabel="Opened"
              />
            )}
          </li>
        </ul>
      </div>
      <div className={styles.rowActions}>
        <CopyButton
          value={share.url}
          label="Copy link"
          className={styles.copy}
          {...(fresh ? {} : { variant: "ghost" as const })}
        />
        <button type="button" className={`${ui.button} ${styles.revoke}`} onClick={onRevoke}>
          Revoke
        </button>
      </div>
    </li>
  );
}

/** Hover shows the exact moment; a tap swaps it in for touch screens. */
function Moment({ iso, label, exactLabel }: { iso: string; label: string; exactLabel: string }) {
  const [exact, setExact] = useState(false);
  const full = formatDateTime(iso);
  return (
    <button
      type="button"
      className={styles.moment}
      title={full}
      aria-pressed={exact}
      onClick={() => setExact((on) => !on)}
    >
      <time dateTime={iso}>{exact ? `${exactLabel} ${full}` : label}</time>
    </button>
  );
}

function EndedShares({ shares }: { shares: ShareLink[] }) {
  return (
    <details className={styles.ended}>
      <summary className={styles.endedSummary}>
        <Icon name="chevron" className={styles.endedChevron} />
        Ended links
        <span className={styles.endedCount}>{shares.length}</span>
      </summary>
      <ul className={styles.endedList}>
        {shares.map((share) => {
          const at = endedAt(share);
          return (
            <li key={share.token} className={styles.endedRow}>
              <span className={styles.endedUrl} title={share.url}>
                {shortShareUrl(share.url)}
              </span>
              <span className={styles.endedWhen}>
                {at ? (
                  <Moment
                    iso={at}
                    label={`${share.state === "revoked" ? "Revoked" : "Expired"} ${formatShortDate(at)}`}
                    exactLabel={share.state === "revoked" ? "Revoked" : "Expired"}
                  />
                ) : share.state === "revoked" ? (
                  "Revoked"
                ) : (
                  "Expired"
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

const DAY_MS = 86_400_000;

function CreateShareForm({
  node,
  onCreated,
  onCancel,
}: {
  node: FileNode;
  onCreated: (token: string) => void;
  onCancel: () => void;
}) {
  const create = useCreateShare(node.id);
  const id = useId();
  const [mode, setMode] = useState<"download" | "stream">("download");
  const [pinned, setPinned] = useState(false);
  const [expiry, setExpiry] = useState<ExpiryChoice>("never");
  const [picked, setPicked] = useState(() => dateInputValue(new Date(Date.now() + 7 * DAY_MS)));
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [tried, setTried] = useState(false);

  const now = new Date();
  const ends = expiryOf(expiry, picked, now);
  const dateBad = expiry === "date" && (ends === null || ends <= now);
  const passwordBad = password.length > 0 && password.length < SHARE_PASSWORD_MIN;
  const isFile = node.kind === "file" && node.currentVersion !== null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (dateBad || passwordBad) return;
    create.mutate(
      {
        mode,
        pinVersion: isFile && pinned,
        ...(password ? { password } : {}),
        ...(ends ? { expiresAt: ends.toISOString() } : {}),
      },
      { onSuccess: (share) => onCreated(share.token) },
    );
  };

  return (
    <form className={styles.form} onSubmit={submit} noValidate aria-label="New share link">
      <div className={styles.field} data-wide={isFile ? undefined : ""}>
        <p id={`${id}-mode`} className={filePage.fieldLabel}>
          Recipients can
        </p>
        <div
          role="radiogroup"
          aria-labelledby={`${id}-mode`}
          className={`${filePage.accessOptions} ${styles.modeOptions}`}
        >
          <AccessOption
            name={`${id}-mode`}
            icon="download"
            label="Download allowed"
            detail="Open and save a copy"
            checked={mode === "download"}
            onPick={() => setMode("download")}
          />
          <AccessOption
            name={`${id}-mode`}
            icon="play"
            label="Stream only"
            detail="Plays in the browser, no download"
            checked={mode === "stream"}
            onPick={() => setMode("stream")}
          />
        </div>
      </div>

      {isFile ? (
        <div className={styles.field}>
          <p id={`${id}-version`} className={filePage.fieldLabel}>
            Version
          </p>
          <Segmented
            name={`${id}-version`}
            labelledBy={`${id}-version`}
            value={pinned ? "pinned" : "latest"}
            onChange={(value) => setPinned(value === "pinned")}
            options={[
              { value: "latest", label: "Always the latest" },
              { value: "pinned", label: `Only version ${node.currentVersion}` },
            ]}
          />
          <p className={styles.hint}>
            {pinned
              ? `New uploads leave the link on version ${node.currentVersion}.`
              : "The link follows each new upload."}
          </p>
        </div>
      ) : null}

      <div className={styles.field}>
        <p id={`${id}-expiry`} className={filePage.fieldLabel}>
          Expires
        </p>
        <Segmented
          name={`${id}-expiry`}
          labelledBy={`${id}-expiry`}
          value={expiry}
          onChange={setExpiry}
          options={EXPIRY_CHOICES}
        />
        <div className={styles.expiryDetail}>
          {expiry === "date" ? (
            <input
              type="date"
              className={`${ui.input} ${styles.dateInput}`}
              aria-label="Last day the link opens"
              aria-invalid={(tried && dateBad) || undefined}
              min={dateInputValue(now)}
              value={picked}
              onChange={(event) => setPicked(event.target.value)}
            />
          ) : null}
          <p className={styles.hint} data-error={(tried && dateBad) || undefined}>
            {tried && dateBad
              ? "Pick today or a later day."
              : expiry === "date" && ends
                ? `Opens through ${formatDate(new Date(ends.getTime() - 1).toISOString())}`
                : ends
                  ? `Stops opening ${formatDateTime(ends.toISOString())}`
                  : "Opens until you revoke it"}
          </p>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-password`} className={styles.labelRow}>
          <span className={filePage.fieldLabel}>Password</span>
          <span className={styles.optional}>Optional</span>
        </label>
        <div className={styles.passwordField}>
          <input
            id={`${id}-password`}
            type={reveal ? "text" : "password"}
            className={`${ui.input} ${styles.passwordInput}`}
            value={password}
            placeholder={`At least ${SHARE_PASSWORD_MIN} characters`}
            autoComplete="new-password"
            spellCheck={false}
            maxLength={200}
            aria-invalid={(tried && passwordBad) || undefined}
            aria-describedby={`${id}-password-hint`}
            onChange={(event) => setPassword(event.target.value)}
          />
          <button
            type="button"
            className={styles.reveal}
            onClick={() => setReveal((on) => !on)}
            aria-label={reveal ? "Hide password" : "Show password"}
            aria-pressed={reveal}
            title={reveal ? "Hide" : "Show"}
          >
            <Icon name={reveal ? "eyeOff" : "eye"} />
          </button>
        </div>
        <p
          id={`${id}-password-hint`}
          className={styles.hint}
          data-error={(tried && passwordBad) || undefined}
        >
          {tried && passwordBad
            ? `Use at least ${SHARE_PASSWORD_MIN} characters, or leave it empty.`
            : password
              ? "Recipients type it once per browser."
              : "Empty opens without a password."}
        </p>
      </div>

      {create.error ? (
        <p className={styles.formError} role="alert">
          <Icon name="alert" />
          The link was not created: {messageOf(create.error)}
        </p>
      ) : null}

      <div className={styles.formActions}>
        <button type="button" className={ui.button} onClick={onCancel} disabled={create.isPending}>
          Cancel
        </button>
        <button
          type="submit"
          className={ui.button}
          data-variant="primary"
          disabled={create.isPending}
        >
          <Icon name="link" />
          {create.isPending ? "Creating…" : "Create link"}
        </button>
      </div>
    </form>
  );
}

type SegmentedProps<T extends string> = {
  name: string;
  labelledBy: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
};

function Segmented<T extends string>({
  name,
  labelledBy,
  value,
  onChange,
  options,
}: SegmentedProps<T>) {
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className={styles.segmented}>
      {options.map((option) => (
        <label
          key={option.value}
          className={styles.segment}
          data-checked={option.value === value || undefined}
        >
          <input
            type="radio"
            name={name}
            className={filePage.accessInput}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}
